import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from "lucide-react";
import { MasterPlan } from "./UserPlanModal";
import userService from "../services/UserService";
import { SbCurrentUser } from "../submission_boards/component/SbModel";
import { showToast } from "../shared/Toast";
import Button from "../submission_boards/component/Button";

interface ContactSalesProps {
  isOpen: boolean;
  currentUser: SbCurrentUser;
  masterPlan: MasterPlan;
  onNoteAdded: (note: string) => void;
  onClose: () => void;
}

const ContactSales: React.FC<ContactSalesProps> = ({
  isOpen,
  currentUser,
  masterPlan,
  onNoteAdded,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSubmissionBoard, setSelectedSubmissionBoard] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    // Access the input element directly using its stable ID
    const emailElement = document.getElementById('add_note');
    if (emailElement) {
      // Assert that the element is an HTMLInputElement before calling focus
      (emailElement as HTMLInputElement).focus();
    }
  }, []);

  const saveClickHandler = async () => {
    if (note.trim() == '') {
      console.log('Note cannot be empty');
      showToast('Note cannot be empty', 'danger');
      return;
    }
    try {
      if (
        masterPlan == undefined ||
        masterPlan == null
      ) {
        showToast('Invalid plan selection.', 'danger');
        throw new Error('Error: Invalid data selection.');
      }
      setLoading(true);
      await userService.contactSalePost(
        note,
        masterPlan
      );
      showToast('Sent note to sales department.', 'success');
      onNoteAdded(note);
      setNote('');
      onClose();
    } catch (error) {
      showToast('Failed to send note', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const onCloseDialog = () => {
    setNote('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative " style={{ zIndex: 20 }} onClose={onCloseDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-customGray" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative flex flex-col absolute  rotate-0 opacity-100   border border-solid border-gray-300  bg-white py-1  rounded-md shadow-xl-0-19-40-8">
                  <div className="justify-items-center text-left">
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                      <div
                        className="bg-white rounded-lg shadow-xl flex flex-col overflow-visible gap-2-1"
                        style={{
                          height: '80vh',
                          maxHeight: '31.25rem',
                          width: '80vw',
                          maxWidth: '31.25rem',
                        }}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-5">
                          <h2 className="text-lg font-medium text-black">Contact Sales</h2>
                          <button
                            className="flex items-center justify-center text-gray-700 hover:text-gray-600 transition-colors h-6 w-6"
                            onClick={onClose}
                          >
                            <XIcon className="w-6 h-6" />
                          </button>
                        </div>

                        {/* Content */}
                        <div
                          className="pb-10 px-10 scrollable-content overflow-y-scroll"
                          style={{}}
                        >
                          <div className="gap-5 flex flex-col">
                            {/* Profile Info */}
                            <div className="flex flex-row gap-2-1 items-center ">
                              <div className="">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  Subscription: {masterPlan.plan_name}
                                </h3>
                              </div>
                            </div>
                            <hr />
                            {/* Add Note Section */}
                            <div className="flex flex-col gap-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Add note*
                              </label>
                              <textarea
                                id="add_note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Enter a note sales team..."
                                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                rows={4}
                              />
                            </div>
                            {/* Footer */}
                            <div className="flex items-center justify-center gap-2-1 pt-2">
                              <Button
                                id="cancel_add_note_button"
                                variant="secondary"
                                onClick={onCloseDialog}
                              // className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                Cancel
                              </Button>
                              <Button
                                id="save_add_note_button"
                                variant="primary"
                                onClick={saveClickHandler}
                                disabled={loading}
                              // className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {loading ? 'Saving...' : 'Submit'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ContactSales;